import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import './OrderDetails.css';

const AdminOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadOrderDetails();
  }, [id]);

  const loadOrderDetails = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_ORDERS, getFetchConfig('GET', null, true));
      
      if (response.ok) {
        const data = await response.json();
        const foundOrder = data.orders?.find(o => o.id.toString() === id);
        if (foundOrder) {
          setOrder(foundOrder);
        } else {
          setMessage('Pedido não encontrado');
        }
      } else {
        setMessage('Erro ao carregar pedido');
      }
    } catch (error) {
      setMessage('Erro ao carregar pedido: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.ADMIN_ORDERS}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setOrder(prev => ({ ...prev, status: newStatus }));
        setMessage('Status atualizado com sucesso!');
      } else {
        setMessage('Erro ao atualizar status');
      }
    } catch (error) {
      setMessage('Erro ao atualizar status: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9500';
      case 'processando': return '#007bff';
      case 'enviado': return '#17a2b8';
      case 'entregue': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const calculateTotal = () => {
    if (!order || !order.items) return 0;
    return order.totalAmount || order.items.reduce((total, item) => total + (item.itemTotal || item.productPrice * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="order-details-container">
        <div className="loading">Carregando detalhes do pedido...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-container">
        <div className="error-message">{message}</div>
        <button 
          onClick={() => navigate('/admin')} 
          className="btn btn-secondary"
        >
          Voltar ao Admin
        </button>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button 
          onClick={() => navigate('/admin')} 
          className="btn btn-secondary"
        >
          ← Voltar ao Admin
        </button>
        <h1>Detalhes do Pedido #{order.id}</h1>
        {message && <div className="status-message">{message}</div>}
      </div>

      <div className="order-details-content">
        {/* Informações do Cliente */}
        <div className="info-section">
          <h2>Informações do Cliente</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nome:</label>
              <span>{order.userId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{order.userEmail || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Telefone:</label>
              <span>{'N/A'}</span>
            </div>
            <div className="info-item full-width">
              <label>Endereço:</label>
              <span>{'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Itens do Pedido */}
        <div className="info-section">
          <h2>Itens do Pedido</h2>
          <div className="items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <h4>{item.productName}</h4>
                    <p>Quantidade: {item.quantity}</p>
                    <p>Preço unitário: €{item.productPrice?.toFixed(2)}</p>
                    <p className="item-total">Subtotal: €{item.itemTotal?.toFixed(2)}</p>
                  </div>
                </div>
              ))
            ) : (
              <p>Nenhum item encontrado</p>
            )}
          </div>
          
          <div className="order-total">
            <h3>Total do Pedido: €{calculateTotal().toFixed(2)}</h3>
          </div>
        </div>

        {/* Status e Datas */}
        <div className="info-section">
          <h2>Status e Informações</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Status Atual:</label>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {order.status?.toUpperCase() || 'PENDENTE'}
              </span>
            </div>
            <div className="info-item">
              <label>Data do Pedido:</label>
              <span>
                {order.createdAt ? new Date(order.createdAt).toLocaleString('pt-PT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <label>Última Atualização:</label>
              <span>
                {order.updatedAt ? new Date(order.updatedAt).toLocaleString('pt-PT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Ações de Admin */}
        <div className="info-section">
          <h2>Ações de Administrador</h2>
          <div className="status-actions">
            <button 
              onClick={() => handleStatusUpdate('pendente')}
              disabled={updating}
              className="btn btn-status pendente"
            >
              Marcar como Pendente
            </button>
            <button 
              onClick={() => handleStatusUpdate('processando')}
              disabled={updating}
              className="btn btn-status processando"
            >
              Marcar como Processando
            </button>
            <button 
              onClick={() => handleStatusUpdate('enviado')}
              disabled={updating}
              className="btn btn-status enviado"
            >
              Marcar como Enviado
            </button>
            <button 
              onClick={() => handleStatusUpdate('entregue')}
              disabled={updating}
              className="btn btn-status entregue"
            >
              Marcar como Entregue
            </button>
            <button 
              onClick={() => handleStatusUpdate('cancelado')}
              disabled={updating}
              className="btn btn-status cancelado"
            >
              Cancelar Pedido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetails;
